import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import useTransitOperationFormSubmit from '../../transit-operation/components/useTransitOperationFormSubmit'
import useConsignmentFormSubmit from '../../consignment/components/useConsignmentFormSubmit'
import useSaveGuaranteeFormValues from '../../guarantee/components/useSaveGuaranteeFormValues'
import useSaveAuthorisationFormValues from '../../authorisation/components/useSaveAuthorisationFormValues'
import useDeleteAuthorisations from '../../authorisation/components/useDeleteAuthorisations'
import { QueryKeys } from '../../../key-registry'
import useSaveConsignmentDocuments from '../../consignment-document/components/useSaveConsignmentDocuments'
import useDeleteConsignmentDocuments from '../../consignment-document/components/useDeleteConsignmentDocuments'
import useDeleteCustomsOfficeOfTransit from '../../customs-office-of-transit/components/useDeleteCustomsOfficeOfTransit'
import { ErrorResult } from '../../../../shared/models/types'
import useDeleteCustomsOfficeOfTransitExit from '../../customs-office-of-transit-exit/components/useDeleteCustomsOfficeOfTransitExit'
import useSaveConsignmentTraderFormValues from './useSaveConsignmentTraderFormValues'
import { Declaration } from '../../models/types'
import { Guarantee } from '../../guarantee/models/types'
import computeConsignmentDocumentDeletes from '../../consignment-document/models/computeConsignmentDocumentDeletes'

interface SaveDeclarationResult {
  declaration: Declaration
  hasPartialDocumentWrite: boolean
  errors: ErrorResult[]
}

interface UseSaveDeclarationFormProps {
  declaration: Declaration
}
function useSaveDeclarationForm({
  declaration,
}: UseSaveDeclarationFormProps) {
  const transitOperationId = declaration?.transitOperation?.id ?? null
  const consignmentId = declaration?.consignment?.id ?? null
  const { saveTransitOperation } = useTransitOperationFormSubmit(transitOperationId)
  const { saveConsignment } = useConsignmentFormSubmit()
  const { saveTraders } = useSaveConsignmentTraderFormValues()
  const { deleteCustomsOfficesOfTransit } = useDeleteCustomsOfficeOfTransit()
  const { deleteCustomsOfficesOfTransitExit } = useDeleteCustomsOfficeOfTransitExit()
  const { saveGuarantees } = useSaveGuaranteeFormValues()
  const { saveAuthorisations } = useSaveAuthorisationFormValues()
  const { deleteAuthorisations } = useDeleteAuthorisations()
  const { saveConsignmentDocuments } = useSaveConsignmentDocuments()
  const { deleteConsignmentDocuments } = useDeleteConsignmentDocuments()
  const queryClient = useQueryClient()
  const [isSaving, setSaving] = useState<boolean>(false)
  const savesInFlightRef = useRef<number>(0)

  const writeDeclaration = async (
    formValues: TransitOperationFormValues,
    isDraft: boolean,
  ): Promise<SaveDeclarationResult> => {
    const consignmentDocumentsToDelete = computeConsignmentDocumentDeletes(
      declaration.consignmentDocuments,
      formValues,
    )
    const errors: ErrorResult[] = []
    // delete first, so the update won't return
    if (transitOperationId != null) {
      const {
        errors: deleteCustomsOfficesOfTransitErrors,
      } = await deleteCustomsOfficesOfTransit(formValues.transitCustomsOffices)
      errors.push(...deleteCustomsOfficesOfTransitErrors)

      const {
        errors: deleteCustomsOfficesOfTransitExitErrors,
      } = await deleteCustomsOfficesOfTransitExit(formValues.customsOfficesOfTransitExit)
      errors.push(...deleteCustomsOfficesOfTransitExitErrors)
    }

    const savedTransitOperation = await saveTransitOperation(formValues)
    if (!savedTransitOperation.id) throw Error('Missing transit operation ID')

    const tradersResult = await saveTraders(savedTransitOperation.id, isDraft, formValues)

    const savedConsignment = await saveConsignment({
      transitOperationId: savedTransitOperation.id,
      consignmentId,
      isDraft,
      formValues,
      consigneeId: tradersResult.consignee?.id ?? null,
      consignorId: tradersResult.consignor?.id ?? null,
      carrierId: tradersResult.carrier?.id ?? null,
    })
    if (!savedConsignment.id) throw Error('Missing consignment ID')

    const {
      data: consignmentDocuments,
      documentErrors: consignmentDocumentSaveErrors,
      errors: consignmentDocumentErrors,
    } = await saveConsignmentDocuments(savedTransitOperation.id, savedConsignment.id, formValues)

    let archiveConsignmentDocumentsErrors: ErrorResult[] = []
    if (!consignmentDocumentSaveErrors.length) {
      const archiveResult = await deleteConsignmentDocuments({ documentsToDelete: consignmentDocumentsToDelete })
      archiveConsignmentDocumentsErrors = archiveResult.errors
    }

    let guarantees: Guarantee[] = []
    let saveGuaranteesErrors: ErrorResult[] = []
    if (formValues.declarationType !== 'ENS') {
      const saveGuaranteesResult = await saveGuarantees(savedTransitOperation.id, [formValues.guarantee], isDraft)
      guarantees = saveGuaranteesResult.saved ?? []
      saveGuaranteesErrors = saveGuaranteesResult.errors ?? []
    }

    const {
      saved: authorisations,
      errors: saveAuthorisationsErrors,
    } = await saveAuthorisations(savedTransitOperation.id, formValues.authorisations)

    const { errors: deleteAuthorisationsErrors } = await deleteAuthorisations(formValues.authorisations
      .filter((o) => o.id != null && o.deleted)
      .map((o) => o.id as number))

    const allErrors = [
      ...errors,
      ...saveGuaranteesErrors,
      ...tradersResult.errors,
      ...consignmentDocumentErrors,
      ...archiveConsignmentDocumentsErrors,
      ...saveAuthorisationsErrors,
      ...deleteAuthorisationsErrors,
    ]

    return {
      declaration: {
        transitOperation: savedTransitOperation,
        consignment: savedConsignment,
        guarantees,
        authorisations,
        consignmentDocuments,
      },
      hasPartialDocumentWrite: consignmentDocumentSaveErrors.length > 0,
      errors: allErrors,
    }
  }

  const updateCaches = async (result: SaveDeclarationResult) => {
    const { declaration: updatedDeclaration } = result
    if (!updatedDeclaration.transitOperation?.id) throw Error('Missing transit operation ID')
    if (!updatedDeclaration.consignment?.id) throw Error('Missing consignment ID')

    queryClient.setQueriesData(
      {
        queryKey: QueryKeys.transitOperation.detailById({ id: updatedDeclaration.transitOperation.id }),
      },
      (prev) => {
        if (!prev) return prev
        return { ...prev, ...updatedDeclaration.transitOperation }
      },
    )

    queryClient.setQueryData(
      QueryKeys.consignment.detailsByParams({ transitOperationId: updatedDeclaration.transitOperation.id }),
      updatedDeclaration.consignment,
    )

    queryClient.setQueryData(
      QueryKeys.guarantee.list({ transitOperationId: updatedDeclaration.transitOperation.id }),
      updatedDeclaration.guarantees,
    )

    queryClient.setQueryData(
      QueryKeys.authorisation.allByParams({ transitOperationId: updatedDeclaration.transitOperation.id }),
      updatedDeclaration.authorisations,
    )

    if (!result.hasPartialDocumentWrite) {
      queryClient.setQueryData(
        QueryKeys.consignmentDocument.byConsignmentId(updatedDeclaration.consignment.id),
        updatedDeclaration.consignmentDocuments,
      )
    }

    await queryClient.invalidateQueries(QueryKeys.transitOperation.validation({ transitOperationId }))
  }

  const saveDeclaration = async ({ values, isDraft } : {
    values: TransitOperationFormValues
    isDraft: boolean
  }) => {
    savesInFlightRef.current += 1
    setSaving(true)
    try {
      const result = await writeDeclaration(values, isDraft)
      await updateCaches(result)
      return result
    } finally {
      savesInFlightRef.current -= 1
      if (savesInFlightRef.current === 0) {
        setSaving(false)
      }
    }
  }

  return {
    saveDeclaration,
    isSubmitting: isSaving,
  }
}

export default useSaveDeclarationForm
